import { BLANK_ADDRESS } from '../geo/addressUtils';

/**
 * A pickup point as the form holds it. The address keys are `AddressValue`'s,
 * so `AddressFields` works on this object as it is.
 */
export const BLANK_PICKUP_POINT = {
  name: '',
  storekeeperName: '',
  storekeeperPhone: '',
  storekeeperEmail: '',
  comments: '',
  allDay: false,
  amFrom: '',
  amTo: '',
  pmFrom: '',
  pmTo: '',
  ...BLANK_ADDRESS,
};

/** `"09:00:00"` → `"09:00"`, which is what an `<input type="time">` holds. */
const clock = (value) => (value ? String(value).slice(0, 5) : '');

/**
 * The form of an existing point, as the API returns it.
 *
 * @param {object} point
 * @returns {typeof BLANK_PICKUP_POINT}
 */
export const formFromPickupPoint = (point) => {
  const address = point?.address || {};
  return {
    ...BLANK_PICKUP_POINT,
    name: point?.name || '',
    storekeeperName: point?.storekeeper_name || '',
    storekeeperPhone: point?.storekeeper_phone || '',
    storekeeperEmail: point?.storekeeper_email || '',
    comments: point?.warehouse_address_comments || '',
    allDay: Boolean(point?.all_day_pickup),
    amFrom: clock(point?.am_pickup_from),
    amTo: clock(point?.am_pickup_to),
    pmFrom: clock(point?.pm_pickup_from),
    pmTo: clock(point?.pm_pickup_to),
    street: address.street || '',
    streetNumber: address.street_number || '',
    region: address.region || '',
    commune: address.commune || '',
    settlement: address.settlement || '',
    settlementId: '',
    postalCode: address.postal_code || '',
  };
};

const blankToNull = (value) => (value ? value : null);

/**
 * What the API takes. Empty times travel as `null`, so clearing a range on
 * edit really clears it (an empty string would be refused as a time).
 *
 * @param {typeof BLANK_PICKUP_POINT} form
 * @param {{countryId?: number|null}} [options]
 */
export const payloadFromForm = (form, { countryId = null } = {}) => ({
  name: form.name.trim(),
  storekeeper_name: form.storekeeperName.trim() || null,
  storekeeper_phone: form.storekeeperPhone.trim() || null,
  storekeeper_email: form.storekeeperEmail.trim() || null,
  warehouse_address_comments: form.comments.trim() || null,
  all_day_pickup: Boolean(form.allDay),
  am_pickup_from: form.allDay ? null : blankToNull(form.amFrom),
  am_pickup_to: form.allDay ? null : blankToNull(form.amTo),
  pm_pickup_from: form.allDay ? null : blankToNull(form.pmFrom),
  pm_pickup_to: form.allDay ? null : blankToNull(form.pmTo),
  address: {
    street: form.street.trim(),
    street_number: String(form.streetNumber).trim(),
    region: form.region,
    commune: form.commune,
    settlement: blankToNull(form.settlement),
    postal_code: blankToNull(form.postalCode),
    ...(countryId ? { country: countryId } : {}),
  },
});

/**
 * The form's own rules, on top of `validateAddress`: a name, and ranges that
 * are either whole or absent, ending after they start.
 *
 * @param {typeof BLANK_PICKUP_POINT} form
 * @returns {Object.<string, string>}
 */
export const validatePickupPoint = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Campo obligatorio';
  if (!form.allDay) {
    [['amFrom', 'amTo'], ['pmFrom', 'pmTo']].forEach(([from, to]) => {
      const a = form[from];
      const b = form[to];
      if (Boolean(a) !== Boolean(b)) errors[from] = 'Indica inicio y fin, o deja ambos vacíos';
      else if (a && b && a >= b) errors[to] = 'El fin debe ser posterior al inicio';
    });
    if (!form.amFrom && !form.pmFrom) errors.amFrom = 'Indica al menos un rango, o marca "Todo el día"';
  }
  return errors;
};

/**
 * "Todo el día", "09:00 a 13:00 y 15:00 a 19:00", or "Sin horario".
 *
 * @param {object} point - As the API returns it.
 * @returns {string}
 */
export const scheduleLabel = (point) => {
  if (point?.all_day_pickup) return 'Todo el día';
  const ranges = [
    [point?.am_pickup_from, point?.am_pickup_to],
    [point?.pm_pickup_from, point?.pm_pickup_to],
  ]
    .filter(([from, to]) => from && to)
    .map(([from, to]) => `${clock(from)} a ${clock(to)}`);
  return ranges.length ? ranges.join(' y ') : 'Sin horario';
};

/*
 * When a shipment's destination can still be changed, and when the point can
 * hand it over. The server is the judge; these keep the menu honest.
 */

/** Before the courier has it: the same window the backend opens. */
export const REDIRECT_STATUSES = ['pending', 'waiting_for_assignment'];

/** Already on its way or at the point; not before, not once it ended. */
const HAND_OVER_BLOCKED_STATUSES = [
  'pending',
  'waiting_for_assignment',
  'created_on_courier',
  'fulfilled',
  'assigned',
  'packed',
  'delivered',
  'failed',
  'returning',
  'returned',
  'compensated',
  'cancelled',
  'damaged',
];

export const canRedirectToPickupPoint = (delivery) =>
  Boolean(delivery?.id) && REDIRECT_STATUSES.includes(delivery.status);

export const canHandOverAtPickupPoint = (delivery) =>
  Boolean(delivery?.pickup_point) &&
  !delivery.pickup_point.handed_over_at &&
  !HAND_OVER_BLOCKED_STATUSES.includes(delivery.status);
